import { matchesDashboardInterviewScope } from "./dashboardData";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom"; // Add this import
import NotesManagerDialog from "../components/NotesManagerDialog"; // Import the new component
import ConfirmationDialog from "../components/ConfirmationDialog"; // Import confirmation dialog
import { authFetch } from "../services/httpClient";
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  CircularProgress,
  Tooltip,
  Stack,
  TextField,
  MenuItem,
  IconButton,
  useTheme,
  Menu,
  ListItemIcon,
  Popper,
  Pagination,
} from "@mui/material";
import NotesIcon from "@mui/icons-material/Notes"; // Import the icon used in the new component
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import rbacApi from "../services/rbacApi";
import {
  matchesPipelineTab,
  scheduledDateKey,
  selectionStatus,
} from "./interviewPipeline";
import "./InterviewList.css";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import { useNotification } from "../../interviewAI/NotificationContext";
import { Link as RouterLink } from "react-router-dom";
import { format } from "date-fns";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
// Legacy report import: import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import { HiDocumentDownload } from "react-icons/hi";
// Legacy report import: import { GrDocumentPdf } from "react-icons/gr";
// Legacy report import: import VideocamIcon from "@mui/icons-material/Videocam";
// import WarningIcon from "@mui/icons-material/Warning";
import {
  getInterviews,
  deleteInterview,
  downloadResume,
  sendInterviewInvite,
  // getRecordingUrl, // Legacy report recordings
  // getInterviewReport, // Legacy PDF report
} from "../services/api";
import { useRBAC } from "../context/RBACContext";
import { RESOURCE_DISPLAY_NAMES, ACTION_NAMES } from "../utils/resourceMapping";

const renderAuditTooltipContent = ({
  primaryLabel,
  primaryValue,
  createdLabel,
  createdByName,
  updatedLabel,
  updatedByName,
}) => {
  const hasCreatedInfo =
    createdLabel !== undefined || createdByName !== undefined;
  const hasUpdatedInfo =
    updatedLabel !== undefined || updatedByName !== undefined;

  return (
    <Box sx={{ p: 0.25 }}>
      <Stack spacing={0.25}>
        {primaryLabel ? (
          <Typography
            variant="caption"
            sx={{ display: "block", whiteSpace: "pre-line" }}
          >
            {`${primaryLabel}: ${primaryValue || "N/A"}`}
          </Typography>
        ) : null}
        {hasCreatedInfo ? (
          <Typography
            variant="caption"
            sx={{ display: "block", whiteSpace: "pre-line" }}
          >
            {`${createdLabel || "Created By"}: ${createdByName || "N/A"}`}
          </Typography>
        ) : null}
        {hasUpdatedInfo ? (
          <Typography
            variant="caption"
            sx={{ display: "block", whiteSpace: "pre-line" }}
          >
            {`${updatedLabel || "Last Updated By"}: ${updatedByName || "N/A"}`}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};

const formatScoreForDisplay = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;

  const [integerPart, fractionPart = ""] = String(numericValue).split(".");
  const tenths = Number(fractionPart[0] || 0);
  const hundredths = Number(fractionPart[1] || 0);
  const roundedTenths = tenths + (hundredths >= 5 ? 1 : 0);

  return roundedTenths === 10
    ? `${Number(integerPart) + 1}.0`
    : `${integerPart}.${roundedTenths}`;
};

const InterviewList = () => {
  const location = useLocation(); // Add this hook
  const [interviews, setInterviews] = useState([]);
  const [filterUsers, setFilterUsers] = useState([]);
  const [filterUsersError, setFilterUsersError] = useState("");
  const [managerFilter, setManagerFilter] = useState("");
  const [recruiterFilter, setRecruiterFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [statusInterview, setStatusInterview] = useState(null);
  const [clock, setClock] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBy, setFilterBy] = useState("All");
  const [dashboardScope, setDashboardScope] = useState({
    selectedUserId: "",
    skipDefaultDateFilter: false,
    pendingOnly: false,
  });
  const [templateIdFilter, setTemplateIdFilter] = useState(null); // for Job Template filter
  const [refreshTrigger, setRefreshTrigger] = useState(0); // 👈 Refresh trigger add kiya
  const [isFromJobOpeningDrilldown, setIsFromJobOpeningDrilldown] =
    useState(false); // Track Job Opening drill-down navigation
  const { notify } = useNotification();
  const theme = useTheme();
  const { hasResourcePermissionByName } = useRBAC();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [tempFromDate, setTempFromDate] = useState("");
  const [tempToDate, setTempToDate] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // Date/Time sort order - drives the backend fetch
  const [sortField, setSortField] = useState(null); // null = no column sort active
  const [columnSortOrder, setColumnSortOrder] = useState("asc"); // Job/Stage/Status secondary sort direction - frontend only, never triggers a refetch
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState(null);
  const dateRangeButtonRef = React.useRef(null);
  const latestRequestIdRef = useRef(0);
  const [isLocationStateProcessed, setIsLocationStateProcessed] =
    useState(false);

  // Check permissions
  const canViewInterviews = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.VIEW_INTERVIEWS,
    ACTION_NAMES.GET_DATA,
  );
  const canScheduleInterview = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.SCHEDULE_INTERVIEW,
    ACTION_NAMES.ADD_NEW,
  );
  const canEditInterview = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.VIEW_INTERVIEWS,
    ACTION_NAMES.UPDATE,
  );
  const canDeleteInterview = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.VIEW_INTERVIEWS,
    ACTION_NAMES.DELETE,
  );

  // --- State for Notes Dialog ---
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [currentInterviewId, setCurrentInterviewId] = useState(null);
  const [currentCandidateName, setCurrentCandidateName] = useState(""); // Store candidate name for dialog title

  // --- State for Confirmation Dialog ---
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // --- State for Actions Menu ---
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuInterviewId, setMenuInterviewId] = useState(null);

  const handleMenuOpen = (event, interviewId) => {
    setMenuAnchor(event.currentTarget);
    setMenuInterviewId(interviewId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuInterviewId(null);
  };

  // --- State for Actions Menu ---

  //   const [reportsAnchor, setReportsAnchor] = useState(null);
  //   const [reportsInterviewId, setReportsInterviewId] = useState(null);
  //
  //   const handleReportsMenuOpen = (event, interviewId) => {
  //     setReportsAnchor(event.currentTarget);
  //     setReportsInterviewId(interviewId);
  //   };
  //
  //   const handleReportsMenuClose = () => {
  //     setReportsAnchor(null);
  //     setReportsInterviewId(null);
  //   };
  //

  // Date Range and Sort Handlers
  const handleOpenDateDropdown = (event) => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setDateRangeAnchorEl(event.currentTarget);
  };

  const handleCloseDateDropdown = () => {
    setDateRangeAnchorEl(null);
  };

  const handleDateRangeApply = () => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setDateRangeAnchorEl(null);
    setTemplateIdFilter(null);
    setIsFromJobOpeningDrilldown(false);
    setDashboardScope((previous) => ({
      ...previous,
      pendingOnly: false,
      skipDefaultDateFilter: false,
    }));
    setFilterBy("All");
    setSearchTerm("");
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleClearDateRange = () => {
    setTempFromDate("");
    setTempToDate("");
    setFromDate("");
    setToDate("");
    setDateRangeAnchorEl(null);
    setTemplateIdFilter(null);
    setIsFromJobOpeningDrilldown(false);
    setFilterBy("All");
    setSearchTerm("");
    setDashboardScope((previous) => ({
      ...previous,
      skipDefaultDateFilter: false,
    }));
    setRefreshTrigger((prev) => prev + 1);
  };

  const toggleSortOrder = () => {
    const newOrder = sortOrder === "desc" ? "asc" : "desc";
    setSortOrder(newOrder);
    setRefreshTrigger((prev) => prev + 1);
  };

  const fetchInterviews = useCallback(async () => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    try {
      setLoading(true);
      setError(null);

      const response = await getInterviews(
        fromDate || undefined,
        toDate || undefined,
        sortOrder,
        templateIdFilter,
        dashboardScope.skipDefaultDateFilter && !fromDate && !toDate,
        dashboardScope.selectedUserId || undefined,
      );

      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      const interviewsList = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      setInterviews(interviewsList);
      if (response?.defaultRange) {
        const defaultFromDate = response.defaultRange.fromDate || "";
        const defaultToDate = response.defaultRange.toDate || "";
        setFromDate(defaultFromDate);
        setToDate(defaultToDate);
        setTempFromDate(defaultFromDate);
        setTempToDate(defaultToDate);
      }
    } catch (err) {
      if (requestId !== latestRequestIdRef.current) {
        return;
      }
      setError(err.message || "Failed to fetch interviews");
      setInterviews([]);
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    sortOrder,
    templateIdFilter,
    dashboardScope.selectedUserId,
    dashboardScope.skipDefaultDateFilter,
    fromDate,
    toDate,
  ]);

  useEffect(() => {
    const navState = location.state;
    setActiveTab("all");
    setDashboardScope({
      selectedUserId: navState?.selectedUserId || "",
      skipDefaultDateFilter: Boolean(navState?.skipDefaultDateFilter),
      pendingOnly: Boolean(navState?.pendingOnly),
    });
    if (navState?.skipDefaultDateFilter) {
      setFromDate("");
      setToDate("");
      setTempFromDate("");
      setTempToDate("");
    }
    if (navState?.filterBy) {
      const { filterBy, filterStatus, templateId, skipDefaultDateFilter } =
        navState;

      setFilterBy(filterBy);

      if (filterBy === "DateRange") {
        setFilterBy("All");
        setSearchTerm("");
        setTemplateIdFilter(null);
        setIsFromJobOpeningDrilldown(false);
        setFromDate(navState.fromDate || "");
        setToDate(navState.toDate || "");
        setTempFromDate(navState.fromDate || "");
        setTempToDate(navState.toDate || "");
      } else if (filterBy === "Date") {
        const [year, month, day] = String(filterStatus || "")
          .split("-")
          .map(Number);
        const dateObj = new Date(year, month - 1, day);
        if (!isNaN(dateObj)) {
          const formatted = format(dateObj, "dd MMM yyyy").toLowerCase();
          setSearchTerm(formatted);
        } else {
          setSearchTerm("");
        }
        setTemplateIdFilter(null);
        setIsFromJobOpeningDrilldown(false);
      } else if (filterBy === "Proctoring Alerts") {
        setSearchTerm("");
        setTemplateIdFilter(null);
        setIsFromJobOpeningDrilldown(false);
      } else if (filterBy === "Job Template") {
        setTemplateIdFilter(templateId || null);
        setSearchTerm(filterStatus || "");
        setFromDate("");
        setToDate("");
        setTempFromDate("");
        setTempToDate("");
        setIsFromJobOpeningDrilldown(
          Boolean(templateId || skipDefaultDateFilter),
        );
      } else {
        setSearchTerm(filterStatus || "");
        setTemplateIdFilter(null);
        setIsFromJobOpeningDrilldown(false);
      }

      window.history.replaceState({}, document.title);
    } else {
      setFilterBy("All");
      setSearchTerm("");
      setTemplateIdFilter(null);
      setIsFromJobOpeningDrilldown(false);
    }

    setIsLocationStateProcessed(true);
  }, [
    location.key,
    location.state,
    location.state?.filterBy,
    location.state?.filterStatus,
    location.state?.templateId,
    location.state?.skipDefaultDateFilter,
  ]);

  useEffect(() => {
    if (!isLocationStateProcessed) return;

    fetchInterviews();
  }, [
    fetchInterviews,
    isLocationStateProcessed,
    refreshTrigger,
    sortOrder,
    templateIdFilter,
    isFromJobOpeningDrilldown,
    dashboardScope,
    fromDate,
    toDate,
  ]);

  // --- Functions for Notes Dialog ---
  const handleOpenNotesDialog = (interviewId) => {
    const interview = interviews.find((i) => i._id === interviewId);
    if (interview) {
      setCurrentInterviewId(interviewId);
      setCurrentCandidateName(interview.candidateName || "Unknown Candidate");
      setNotesDialogOpen(true);
    }
  };

  const handleCloseNotesDialog = () => {
    setNotesDialogOpen(false);
    setCurrentInterviewId(null);
    setCurrentCandidateName("");
  };

  const parseLocalDate = (dateString) => {
    const match = String(dateString || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const [, year, month, day] = match.map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
      ? date
      : null;
  };

  const handleDelete = (id) => {
    if (!id || id === "undefined") return;
    setPendingDeleteId(id);
    setConfirmationOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await deleteInterview(pendingDeleteId); // This function should ideally return the response or throw an error with details
      // If deleteInterview throws an error with response details, it will be caught below
      setInterviews((prev) =>
        prev.filter((interview) => interview._id !== pendingDeleteId),
      );
      notify("success", "Interview deleted");
    } catch (err) {
      // --- IMPROVED ERROR HANDLING FOR DELETE ---
      let errorMessage = "Error deleting interview."; // Default message
      if (err.response) {
        // Server responded with error status
        const responseData = err.response.data;
        if (responseData && typeof responseData === "object") {
          if (responseData.detail) {
            // Handle string detail or array of Pydantic errors
            if (typeof responseData.detail === "string") {
              errorMessage = responseData.detail;
            } else if (
              Array.isArray(responseData.detail) &&
              responseData.detail[0]?.msg
            ) {
              errorMessage = responseData.detail[0].msg;
            }
          } else if (responseData.message) {
            errorMessage = responseData.message;
          }
        }
        // If no specific detail found, use status text or generic message based on status
        if (errorMessage === "Error deleting interview.") {
          errorMessage += ` ${err.response.statusText || `Status ${err.response.status}`}`;
        }
      } else if (err.request) {
        // Request made but no response received
        errorMessage =
          "No response received from the server. Please check your connection.";
      } else {
        // Other errors
        errorMessage = `Request setup error: ${err.message}`;
      }
      notify("error", errorMessage);
    } finally {
      setPendingDeleteId(null);
    }
  };

  const handleDownloadResume = async (id) => {
    try {
      const response = await downloadResume(id);
      const blob = response.data;
      const disposition = response.headers["content-disposition"];
      const filename =
        disposition?.match(/filename="(.+)"/)?.[1] || "resume.pdf";

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // --- IMPROVED ERROR HANDLING FOR DOWNLOAD RESUME ---
      let errorMessage = "Download error."; // Default message
      if (error.response) {
        // Server responded with error status
        const responseData = error.response.data;
        if (responseData && typeof responseData === "object") {
          if (responseData.detail) {
            if (typeof responseData.detail === "string") {
              errorMessage = responseData.detail;
            } else if (
              Array.isArray(responseData.detail) &&
              responseData.detail[0]?.msg
            ) {
              errorMessage = responseData.detail[0].msg;
            }
          } else if (responseData.message) {
            errorMessage = responseData.message;
          }
        }
        if (errorMessage === "Download error.") {
          errorMessage += ` ${error.response.statusText || `Status ${error.response.status}`}`;
        }
      } else if (error.request) {
        errorMessage = "No response received from the server.";
      } else {
        errorMessage = `Request setup error: ${error.message}`;
      }
      notify("error", errorMessage);
      // --- END IMPROVED ERROR HANDLING FOR DOWNLOAD RESUME ---
    }
  };

  const handleSendInvite = async (interview) => {
    if (!interview || interview.job_application_status === "Completed") {
      notify("error", "Invitations cannot be sent for completed interviews.");
      return;
    }

    try {
      const payload = {
        candidate_id: interview._id,
        template_id: interview.jobTemplate?._id || "",
        email: interview.candidateEmail,
      };
      const data = await sendInterviewInvite(payload);
      notify("success", data.message || "Invite sent");
    } catch (error) {
      // --- IMPROVED ERROR HANDLING FOR SEND INVITE ---
      let errorMessage = "Failed to send invite."; // Or just a generic fallback like "An error occurred."
      if (error.response) {
        const responseData = error.response.data;
        if (responseData && typeof responseData === "object") {
          if (responseData.detail) {
            if (typeof responseData.detail === "string") {
              errorMessage = responseData.detail; // Use the server message directly
            } else if (
              Array.isArray(responseData.detail) &&
              responseData.detail[0]?.msg
            ) {
              errorMessage = responseData.detail[0].msg; // Use the server message directly
            }
          } else if (responseData.message) {
            errorMessage = responseData.message; // Use the server message directly
          }
        }
        // If no specific detail found, use status text or generic message based on status
        if (errorMessage === "Failed to send invite.") {
          // Or whatever fallback you chose
          errorMessage += ` ${error.response.statusText || `Status ${error.response.status}`}`;
        }
      } else if (error.request) {
        errorMessage =
          "No response received from the server. Please check your connection.";
      } else {
        errorMessage = `Request setup error: ${error.message}`;
      }
      notify("error", errorMessage); // This now passes only the clean server message or fallback
      // --- END IMPROVED ERROR HANDLING FOR SEND INVITE ---
    }
  };

  const handleStatusChange = async (interviewId, newStatus) => {
    // Guard: only allow status change when stage is "Completed"
    const interview = interviews.find((i) => i._id === interviewId);
    if (!interview || interview.job_application_status !== "Completed") {
      notify(
        "error",
        "Status can only be changed when the stage is Completed.",
      );
      return;
    }
    // Guard: only allow SHORTLISTED or REJECTED
    if (newStatus !== "SHORTLISTED" && newStatus !== "REJECTED") {
      notify(
        "error",
        "Invalid status. Only SHORTLISTED or REJECTED are allowed.",
      );
      return;
    }
    try {
      // API call to update selection status - PATCH method use karen
      const response = await authFetch(
        `${process.env.REACT_APP_REST_BASE_URL}/interviews/selection-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            interviewId: interviewId,
            selectionStatus: newStatus,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update status");
      }

      // Update local state after successful API update
      setInterviews((prev) =>
        prev.map((interview) =>
          interview._id === interviewId
            ? {
                ...interview,
                selectionStatus: newStatus,
                candidate_selection_status:
                  newStatus === "SHORTLISTED" ? "pass" : "fail", // 👈 Ye bhi update karen
              }
            : interview,
        ),
      );

      notify("success", `Candidate ${newStatus.toLowerCase()} successfully`);

      // Refresh data to ensure consistency
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error updating selection status:", error);
      notify("error", "Failed to update status: " + error.message);
    }
  };

  //   const handleViewReport = async (interviewId) => {
  //     try {
  //       const pdfBlob = await getInterviewReport(interviewId);
  //       const pdfUrl = window.URL.createObjectURL(
  //         new Blob([pdfBlob], { type: "application/pdf" })
  //       );
  //       window.open(pdfUrl, "_blank", "noopener,noreferrer");
  //     } catch (error) {
  //       // --- IMPROVED ERROR HANDLING FOR VIEW REPORT ---
  //       let errorMessage = "Failed to load report."; // Or just a generic fallback
  //       if (error.response) {
  //         const responseData = error.response.data;
  //         if (responseData && typeof responseData === 'object') {
  //           if (responseData.detail) {
  //             if (typeof responseData.detail === 'string') {
  //               errorMessage = responseData.detail; // Use the server message directly
  //             } else if (Array.isArray(responseData.detail) && responseData.detail[0]?.msg) {
  //               errorMessage = responseData.detail[0].msg; // Use the server message directly
  //             }
  //           } else if (responseData.message) {
  //             errorMessage = responseData.message; // Use the server message directly
  //           }
  //         }
  //         // If no specific detail found, use status text or generic message based on status
  //         if (errorMessage === "Failed to load report.") { // Or whatever fallback you chose
  //           errorMessage += ` ${error.response.statusText || `Status ${error.response.status}`}`;
  //         }
  //       } else if (error.request) {
  //         errorMessage = "No response received from the server. Please check your connection.";
  //       } else {
  //         errorMessage = `Request setup error: ${error.message}`;
  //       }
  //       notify("error", errorMessage); // This now passes only the clean server message or fallback
  //       // --- END IMPROVED ERROR HANDLING FOR VIEW REPORT ---
  //     }
  //   };
  //
  //   const handleViewSummaryPDF = async (interviewId) => {
  //     try {
  //       const baseURL = process.env.REACT_APP_REST_BASE_URL;
  //       const pdfUrl = `${baseURL}/storage/summary-pdf/${interviewId}`;
  //       const response = await authFetch(pdfUrl, {
  //         method: "GET",
  //       });
  //
  //       if (!response.ok) {
  //         throw new Error(`Status: ${response.status}`);
  //       }
  //
  //       const pdfBlob = await response.blob();
  //       const pdfUrlObject = window.URL.createObjectURL(pdfBlob);
  //       window.open(pdfUrlObject, "_blank", "noopener,noreferrer");
  //     } catch (error) {
  //       let errorMessage = "Failed to load summary report.";
  //       if (error.response) {
  //         const responseData = error.response.data;
  //         if (responseData && typeof responseData === 'object') {
  //           if (responseData.detail) {
  //             if (typeof responseData.detail === 'string') {
  //               errorMessage = responseData.detail;
  //             } else if (Array.isArray(responseData.detail) && responseData.detail[0]?.msg) {
  //               errorMessage = responseData.detail[0].msg;
  //             }
  //           } else if (responseData.message) {
  //             errorMessage = responseData.message;
  //           }
  //         }
  //         if (errorMessage === "Failed to load summary report.") {
  //           errorMessage += ` ${error.response.statusText || `Status ${error.response.status}`}`;
  //         }
  //       } else if (error.request) {
  //         errorMessage = "No response received from the server. Please check your connection.";
  //       } else {
  //         errorMessage = `Request setup error: ${error.message}`;
  //       }
  //       notify("error", errorMessage);
  //     }
  //   };
  //
  //   const handleViewTranscriptPDF = async (interviewId) => {
  //     try {
  //       const baseURL = process.env.REACT_APP_REST_BASE_URL;
  //       const pdfUrl = `${baseURL}/storage/transcript-pdf/${interviewId}`;
  //       const response = await authFetch(pdfUrl, {
  //         method: "GET",
  //       });
  //
  //       if (!response.ok) {
  //         throw new Error(`Status: ${response.status}`);
  //       }
  //
  //       const pdfBlob = await response.blob();
  //       const pdfUrlObject = window.URL.createObjectURL(pdfBlob);
  //       window.open(pdfUrlObject, "_blank", "noopener,noreferrer");
  //     } catch (error) {
  //       let errorMessage = "Failed to load transcript report.";
  //       if (error.response) {
  //         const responseData = error.response.data;
  //         if (responseData && typeof responseData === 'object') {
  //           if (responseData.detail) {
  //             if (typeof responseData.detail === 'string') {
  //               errorMessage = responseData.detail;
  //             } else if (Array.isArray(responseData.detail) && responseData.detail[0]?.msg) {
  //               errorMessage = responseData.detail[0].msg;
  //             }
  //           } else if (responseData.message) {
  //             errorMessage = responseData.message;
  //           }
  //         }
  //         if (errorMessage === "Failed to load transcript report.") {
  //           errorMessage += ` ${error.response.statusText || `Status ${error.response.status}`}`;
  //         }
  //       } else if (error.request) {
  //         errorMessage = "No response received from the server. Please check your connection.";
  //       } else {
  //         errorMessage = `Request setup error: ${error.message}`;
  //       }
  //       notify("error", errorMessage);
  //     }
  //   };
  //
  //   const handleViewRecording = async (interviewId) => {
  //     try {
  //       const { videoUrl } = await getRecordingUrl(interviewId);
  //       if (videoUrl) {
  //         window.open(videoUrl, "_blank", "noopener,noreferrer");
  //       } else {
  //         notify("error", "Recording URL not found");
  //       }
  //     } catch (error) {
  //       // --- IMPROVED ERROR HANDLING FOR VIEW RECORDING ---
  //       let errorMessage = "Failed to load recording."; // Or just a generic fallback
  //       if (error.response) {
  //         const responseData = error.response.data;
  //         if (responseData && typeof responseData === 'object') {
  //           if (responseData.detail) {
  //             if (typeof responseData.detail === 'string') {
  //               errorMessage = responseData.detail; // Use the server message directly
  //             } else if (Array.isArray(responseData.detail) && responseData.detail[0]?.msg) {
  //               errorMessage = responseData.detail[0].msg; // Use the server message directly
  //             }
  //           } else if (responseData.message) {
  //             errorMessage = responseData.message; // Use the server message directly
  //           }
  //         }
  //         // If no specific detail found, use status text or generic message based on status
  //         if (errorMessage === "Failed to load recording.") { // Or whatever fallback you chose
  //           errorMessage += ` ${error.response.statusText || `Status ${error.response.status}`}`;
  //         }
  //       } else if (error.request) {
  //         errorMessage = "No response received from the server. Please check your connection.";
  //       } else {
  //         errorMessage = `Request setup error: ${error.message}`;
  //       }
  //       notify("error", errorMessage); // This now passes only the clean server message or fallback
  //       // --- END IMPROVED ERROR HANDLING FOR VIEW RECORDING ---
  //     }
  //   };
  //   const handleViewCam2Recording = async (interviewId) => {
  //     try {
  //       const { secondCameraUrl } = await getRecordingUrl(interviewId);
  //       if (secondCameraUrl) {
  //         window.open(secondCameraUrl, "_blank", "noopener,noreferrer");
  //       } else {
  //         notify("error", "Cam-2 recording not found for this interview");
  //       }
  //     } catch (error) {
  //       // --- IMPROVED ERROR HANDLING FOR VIEW CAM-2 RECORDING ---
  //       let errorMessage = "Failed to load Cam-2 recording."; // Or just a generic fallback
  //       if (error.response) {
  //         const responseData = error.response.data;
  //         if (responseData && typeof responseData === 'object') {
  //           if (responseData.detail) {
  //             if (typeof responseData.detail === 'string') {
  //               errorMessage = responseData.detail; // Use the server message directly
  //             } else if (Array.isArray(responseData.detail) && responseData.detail[0]?.msg) {
  //               errorMessage = responseData.detail[0].msg; // Use the server message directly
  //             }
  //           } else if (responseData.message) {
  //             errorMessage = responseData.message; // Use the server message directly
  //           }
  //         }
  //         // If no specific detail found, use status text or generic message based on status
  //         if (errorMessage === "Failed to load Cam-2 recording.") { // Or whatever fallback you chose
  //           errorMessage += ` ${error.response.statusText || `Status ${error.response.status}`}`;
  //         }
  //       } else if (error.request) {
  //         errorMessage = "No response received from the server. Please check your connection.";
  //       } else {
  //         errorMessage = `Request setup error: ${error.message}`;
  //       }
  //       notify("error", errorMessage); // This now passes only the clean server message or fallback
  //       // --- END IMPROVED ERROR HANDLING FOR VIEW CAM-2 RECORDING ---
  //     }
  //   };
  //

  const handleViewComprehensiveReport = (interviewId) => {
    const videoUrl = `${window.location.origin}/interview-video/${interviewId}`;
    window.open(videoUrl, "_blank", "noopener,noreferrer");
  };

  // helpers for formatted date/time strings (lowercase for searching)
  const formatDateString = (interview) => {
    const date = parseLocalDate(interview?.interviewDate);
    return date ? format(date, "dd MMM yyyy").toLowerCase() : "";
  };
  const formatTimeString = (interview) => {
    if (!interview?.interviewTime) return "";

    const time = String(interview.interviewTime).trim();

    // Expected backend format: HH:mm
    const match = time.match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);

    if (!match) {
      return time.toLowerCase();
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return time.toLowerCase();
    }

    const date = new Date(1970, 0, 1, hours, minutes);

    return format(date, "h:mm a").toLowerCase();
  };

  const userName = (user) =>
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    user?.email ||
    user?.userId ||
    "\u2014";
  const creatorIdOf = (interview) =>
    interview.created_by || interview.createdBy || "";
  const creatorOf = (interview) =>
    filterUsers.find((user) => user.userId === creatorIdOf(interview));
  const managerIdOf = (interview) =>
    interview.manager_id ||
    interview.managerId ||
    creatorOf(interview)?.manager_id ||
    creatorOf(interview)?.managerId ||
    interview.jobTemplate?.manager_id ||
    "";
  const managerNameOf = (interview) =>
    interview.managerName ||
    interview.jobTemplate?.managerName ||
    userName(
      filterUsers.find((user) => user.userId === managerIdOf(interview)),
    );
  const recruiterNameOf = (interview) =>
    interview.createdByName || userName(creatorOf(interview));
  const hierarchyUsers = new Map(
    filterUsers.map((user) => [user.userId, user]),
  );
  const reportingManagerIds = new Set(
    filterUsers
      .map((user) => user.manager_id || user.managerId)
      .filter(Boolean),
  );
  const isInBranch = (userId, managerId) => {
    const visited = new Set();
    let user = hierarchyUsers.get(userId);
    while (user && !visited.has(user.userId)) {
      if (user.userId === managerId || user.ancestorIds?.includes(managerId))
        return true;
      visited.add(user.userId);
      user = hierarchyUsers.get(user.manager_id || user.managerId);
    }
    return false;
  };
  const branchUsers = filterUsers.filter(
    (user) =>
      !dashboardScope.selectedUserId ||
      isInBranch(user.userId, dashboardScope.selectedUserId),
  );
  const roleOf = (user) =>
    String(user.roleName || user.role?.name || "")
      .toLowerCase()
      .replace(/[\s_-]/g, "");
  const managers = branchUsers.filter(
    (user) =>
      user.filterType === "manager" ||
      (user.filterType === undefined &&
        roleOf(user) !== "superadmin" &&
        (roleOf(user).includes("manager") ||
          roleOf(user) === "admin" ||
          reportingManagerIds.has(user.userId))),
  );
  const recruiters = branchUsers.filter(
    (user) =>
      (user.filterType === "recruiter" ||
        (user.filterType === undefined &&
          (roleOf(user) === "recruiter" ||
            (roleOf(user) === "user" &&
              (user.manager_id || user.managerId) &&
              !reportingManagerIds.has(user.userId))))) &&
      (!managerFilter || isInBranch(user.userId, managerFilter)),
  );
  useEffect(() => {
    let alive = true;
    rbacApi
      .get("/templates/filter-users")
      .then((response) => {
        if (alive) {
          setFilterUsers(Array.isArray(response.data) ? response.data : []);
          setFilterUsersError("");
        }
      })
      .catch(() => {
        if (alive) setFilterUsersError("Team filters could not be loaded.");
      });
    return () => {
      alive = false;
    };
  }, [refreshTrigger]);
  useEffect(() => {
    setManagerFilter("");
    setRecruiterFilter("");
  }, [dashboardScope.selectedUserId]);
  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    setPage(1);
  }, [
    activeTab,
    managerFilter,
    recruiterFilter,
    stageFilter,
    searchTerm,
    filterBy,
    fromDate,
    toDate,
    dashboardScope.selectedUserId,
  ]);
  const scopedInterviews = interviews.filter((interview) => {
    if (
      managerFilter &&
      !isInBranch(creatorIdOf(interview), managerFilter) &&
      !isInBranch(managerIdOf(interview), managerFilter)
    )
      return false;
    if (recruiterFilter && creatorIdOf(interview) !== recruiterFilter)
      return false;
    if (stageFilter) {
      const matchesStatus = ["PENDING", "SHORTLISTED", "REJECTED"].includes(
        stageFilter,
      )
        ? selectionStatus(interview) === stageFilter
        : interview.job_application_status === stageFilter;
      if (!matchesStatus) return false;
    }
    const date = scheduledDateKey(interview);
    if ((fromDate || toDate) && !date) return false;
    return (!fromDate || date >= fromDate) && (!toDate || date <= toDate);
  });
  const pipelineTabs = [
    {
      id: "ongoing",
      label: "On-going",
      title: "On-going Interviews",
      subtitle: "Interviews being monitored right now.",
    },
    {
      id: "today",
      label: "Pending Today",
      title: "Pending Today",
      subtitle: "Interviews scheduled for today, not yet started.",
    },
    {
      id: "upcoming",
      label: "Upcoming",
      title: "Upcoming Interviews",
      subtitle: "Interviews scheduled ahead.",
    },
    {
      id: "completed",
      label: "Completed",
      title: "Completed Interviews",
      subtitle: "Completed interviews and candidate results.",
    },
  ];
  const pipelineCounts = Object.fromEntries(
    pipelineTabs.map((tab) => [
      tab.id,
      scopedInterviews.filter((interview) =>
        matchesPipelineTab(interview, tab.id, clock),
      ).length,
    ]),
  );
  const activePipeline = pipelineTabs.find((tab) => tab.id === activeTab) || {
    title: "All Interviews",
    subtitle: "Interviews matching your selected filters.",
  };
  const hasAppliedFilters = Boolean(
    managerFilter ||
      recruiterFilter ||
      stageFilter ||
      searchTerm.trim() ||
      filterBy !== "All" ||
      templateIdFilter ||
      dashboardScope.selectedUserId ||
      dashboardScope.pendingOnly,
  );
  const filteredInterviews = scopedInterviews.filter((interview) => {
    if (!matchesPipelineTab(interview, activeTab, clock)) return false;

    if (
      !matchesDashboardInterviewScope(
        interview,
        dashboardScope,
        fromDate,
        toDate,
      )
    )
      return false;
    const term = searchTerm?.trim().toLowerCase();

    const candidateMatch =
      (interview.candidateName &&
        interview.candidateName.toLowerCase().includes(term)) ||
      (interview.candidateEmail &&
        interview.candidateEmail.toLowerCase().includes(term));

    const roleMatch =
      !!interview.jobTemplate &&
      typeof interview.jobTemplate.name === "string" &&
      interview.jobTemplate.name.toLowerCase().includes(term);

    const reqIdMatch =
      !!interview.jobTemplate?.reqId &&
      String(interview.jobTemplate.reqId).toLowerCase().includes(term);

    const dateMatch = formatDateString(interview).includes(term);
    const timeMatch = formatTimeString(interview).includes(term);

    const stageMatch =
      interview.job_application_status &&
      interview.job_application_status.toLowerCase().includes(term);

    const statusMatch = selectionStatus(interview).toLowerCase().includes(term);

    const scoreMatch =
      (interview.candidate_score_percentage &&
        interview.candidate_score_percentage
          .toString()
          .toLowerCase()
          .includes(term)) ||
      (interview.candidate_selection_status &&
        interview.candidate_selection_status.toLowerCase().includes(term));

    const proctoringMatch = interview.proctoringAlert === true;

    const templateIdMatches =
      Boolean(templateIdFilter) &&
      [
        interview.templateId,
        interview.jobTemplate?._id,
        interview.jobTemplate?.id,
      ].some((value) => String(value) === String(templateIdFilter));

    const hasActiveSearch = Boolean(term);

    switch (filterBy) {
      case "Candidate":
        return hasActiveSearch ? candidateMatch : true;
      case "Req Id":
        return hasActiveSearch ? reqIdMatch : true;
      case "Job Openings":
        return hasActiveSearch ? roleMatch : true;
      case "Job Template":
        return templateIdMatches;
      case "Date":
        return hasActiveSearch ? dateMatch : true;
      case "Time":
        return hasActiveSearch ? timeMatch : true;
      case "Score":
        return hasActiveSearch ? scoreMatch : true;
      case "Stage":
        return hasActiveSearch ? stageMatch : true;
      case "Status":
        return hasActiveSearch ? statusMatch : true;
      case "Proctoring Alerts":
        return proctoringMatch;
      case "All":
      default:
        if (!hasActiveSearch && !templateIdFilter) return true;
        return (
          candidateMatch ||
          reqIdMatch ||
          roleMatch ||
          dateMatch ||
          timeMatch ||
          scoreMatch ||
          stageMatch ||
          statusMatch ||
          (term === "true" && proctoringMatch) ||
          (term === "false" && interview.proctoringAlert === false)
        );
    }
  });

  const getSelectionStatus = selectionStatus;

  // --- Sorting Logic ---
  const getCombinedPriority = (interview) => {
    const stage = interview.job_application_status || "";
    const status = getSelectionStatus(interview);

    // Exact Stage + Status priority
    if (stage === "Scheduled" && status === "PENDING") return 1;
    if (stage === "Invite Sent" && status === "PENDING") return 2;
    if (stage === "In Progress" && status === "PENDING") return 3;
    if (stage === "Completed" && status === "SHORTLISTED") return 4;
    if (stage === "Completed" && status === "REJECTED") return 5;

    // Fallback for unexpected combinations
    return 99;
  };
  // Returns a numeric timestamp for an interview's date + time, using the
  // actual interviewDate/interviewTime values (not formatted display strings).
  // This is the PRIMARY comparator for Job/Stage/Status sorting - it must
  // always order Latest -> Oldest, regardless of the selected column's
  // sort direction.
  const getInterviewDateTime = (interview) => {
    if (!interview?.interviewDate) return 0;

    const datePart = String(interview.interviewDate).trim();
    const rawTime = String(interview?.interviewTime ?? "").trim();
    const timePart = /^\d{2}:\d{2}$/.test(rawTime) ? rawTime : "00:00";

    const timestamp = new Date(`${datePart}T${timePart}:00`).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  const handleSort = (field) => {
    // Clicking the same column again resets to default Date/Time sorting.
    if (sortField === field) {
      setSortField(null);
      setColumnSortOrder("asc");
      return;
    }

    // First click on a column enables its sorting.
    setSortField(field);
    setColumnSortOrder("asc");
  };

  const sortedInterviews = [...filteredInterviews].sort((a, b) => {
    // IMPORTANT:
    // No column selected = keep backend Date/Time ordering exactly as-is.
    if (!sortField) return 0;

    const dateTimeTieBreak = getInterviewDateTime(b) - getInterviewDateTime(a);

    const dir = columnSortOrder === "asc" ? 1 : -1;

    // ---------------------------------------------------------
    // JOB OPENING SORT
    // Date/Time remains PRIMARY.
    // Job Opening is only the secondary tie-breaker.
    // ---------------------------------------------------------
    if (sortField === "job") {
      if (dateTimeTieBreak !== 0) {
        return dateTimeTieBreak;
      }

      const aVal =
        `${a.jobTemplate?.name ?? ""} ${a.jobTemplate?.reqId ?? ""}`.toLowerCase();

      const bVal =
        `${b.jobTemplate?.name ?? ""} ${b.jobTemplate?.reqId ?? ""}`.toLowerCase();

      return aVal.localeCompare(bVal) * dir;
    }

    // ---------------------------------------------------------
    // STAGE / STATUS SORT
    // Stage + Status priority is PRIMARY.
    // Date/Time Latest -> Oldest is SECONDARY.
    // ---------------------------------------------------------
    if (sortField === "stageStatus") {
      const priorityA = getCombinedPriority(a);
      const priorityB = getCombinedPriority(b);

      // First: Stage + Status priority
      if (priorityA !== priorityB) {
        return (priorityA - priorityB) * dir;
      }

      // Same Stage + Status:
      // always Latest -> Oldest
      if (dateTimeTieBreak !== 0) {
        return dateTimeTieBreak;
      }

      return 0;
    }

    return 0;
  });
  // --- End Sorting Logic ---

  const pageCount = Math.max(1, Math.ceil(sortedInterviews.length / 15));
  const currentPage = Math.min(page, pageCount);
  const visibleInterviews = sortedInterviews.slice(
    (currentPage - 1) * 15,
    currentPage * 15,
  );
  const menuInterview = interviews.find(
    (interview) => interview._id === menuInterviewId,
  );
  const menuCompleted = menuInterview?.job_application_status === "Completed";
  const canEditMenuInterview = canEditInterview && !menuCompleted;
  const resetFilters = () => {
    setManagerFilter("");
    setRecruiterFilter("");
    setStageFilter("");
    setSearchTerm("");
    setFilterBy("All");
    setFromDate("");
    setToDate("");
    setTempFromDate("");
    setTempToDate("");
    setTemplateIdFilter(null);
    setIsFromJobOpeningDrilldown(false);
    setDashboardScope((previous) => ({
      ...previous,
      pendingOnly: false,
      skipDefaultDateFilter: false,
    }));
  };
  const selectPipelineTab = (tab) => {
    if (activeTab === "all") {
      resetFilters();
    }
    setActiveTab(tab);
  };
  if (!canViewInterviews) {
    return (
      <Container
        maxWidth={false}
        disableGutters
        sx={{ mt: 8, textAlign: "center", py: 6 }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: "#1976d2", mb: 2 }}
        >
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You don't have access to view interviews. Contact your administrator.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters className="tt-interviews-page">
      <main className="tt-interviews-main">
        <header className="tti-heading">
          <div>
            <span className="tti-eyebrow">PIPELINE</span>
            <h1>Interviews</h1>
            <p>Track and manage all interviews across your teams.</p>
          </div>
          <div className="tti-heading-actions">
            <label className="tti-sr-only" htmlFor="tti-team">
              Filter interviews by team
            </label>
            <select
              id="tti-team"
              value={dashboardScope.selectedUserId}
              onChange={(event) => {
                resetFilters();
                setDashboardScope((previous) => ({
                  ...previous,
                  selectedUserId: event.target.value,
                  pendingOnly: false,
                  skipDefaultDateFilter: false,
                }));
              }}
            >
              <option value="">All teams</option>
              {filterUsers.map((user) => (
                <option key={user.userId} value={user.userId}>
                  {userName(user)}'s team
                </option>
              ))}
            </select>
            <Tooltip
              title={
                !canScheduleInterview
                  ? "You don't have permission to schedule interviews"
                  : "Schedule an interview"
              }
            >
              <span>
                <Button
                  className="tti-primary"
                  variant="contained"
                  startIcon={<AddIcon />}
                  component={canScheduleInterview ? RouterLink : "button"}
                  to={canScheduleInterview ? "/interviews/new" : undefined}
                  disabled={!canScheduleInterview}
                >
                  Schedule new
                </Button>
              </span>
            </Tooltip>
          </div>
        </header>
        <div
          className="tti-tabs"
          role="tablist"
          aria-label="Interview pipeline"
        >
          {pipelineTabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls="tti-results"
              className={`tti-tab ${activeTab === tab.id ? "is-selected" : ""}`}
              onClick={() => selectPipelineTab(tab.id)}
            >
              {tab.id === "ongoing" && <i className="tti-live-dot" />}
              <span>{tab.label}</span>
              <strong>{loading ? "\u2014" : pipelineCounts[tab.id]}</strong>
            </button>
          ))}
        </div>
        <section className="tti-filters" aria-labelledby="tti-filters-heading">
          <h2 id="tti-filters-heading">Filters</h2>
          <div className="tti-filter-grid">
            <label>
              Managers
              <select
                value={managerFilter}
                onChange={(event) => {
                  setManagerFilter(event.target.value);
                  setRecruiterFilter("");
                }}
              >
                <option value="">All Managers</option>
                {managers.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {userName(user)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Recruiter
              <select
                value={recruiterFilter}
                onChange={(event) => setRecruiterFilter(event.target.value)}
              >
                <option value="">All Recruiter</option>
                {recruiters.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {userName(user)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Interview Status
              <select
                value={stageFilter}
                onChange={(event) => setStageFilter(event.target.value)}
              >
                <option value="">All Status</option>
                <optgroup label="Stage">
                  {["Scheduled", "Invite Sent", "In Progress", "Completed"].map(
                    (stage) => (
                      <option key={stage} value={stage}>
                        {stage}
                      </option>
                    ),
                  )}
                </optgroup>
                <optgroup label="Status">
                  {["PENDING", "SHORTLISTED", "REJECTED"].map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0) + status.slice(1).toLowerCase()}
                    </option>
                  ))}
                </optgroup>
              </select>
            </label>
            <div className="tti-date-field">
              <label>Date Range</label>
              <button ref={dateRangeButtonRef} onClick={handleOpenDateDropdown}>
                <span>
                  {fromDate || toDate
                    ? `${fromDate || "Start"} \u2013 ${toDate || "End"}`
                    : "Select Date Range"}
                </span>
                <CalendarTodayIcon />
              </button>
            </div>
            <div className="tti-filter-buttons">
              <Button variant="outlined" onClick={resetFilters}>
                Reset
              </Button>
              <Button
                variant="contained"
                className="tti-primary"
                onClick={() => setRefreshTrigger((value) => value + 1)}
              >
                Apply
              </Button>
            </div>
          </div>
          {filterUsersError && (
            <p role="status" className="tti-filter-error">
              {filterUsersError}
              <button onClick={() => setRefreshTrigger((value) => value + 1)}>
                Retry
              </button>
            </p>
          )}
        </section>
        <Popper
          open={Boolean(dateRangeAnchorEl)}
          anchorEl={dateRangeAnchorEl}
          placement="bottom-start"
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
            {
              name: "flip",
              options: {
                padding: 8,
              },
            },
          ]}
          style={{ zIndex: 1300 }}
        >
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              boxShadow: "0 12px 32px rgba(0, 0, 0, 0.15)",
              minWidth: 320,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                mb: 2,
                color: "#4c13a5",
              }}
            >
              Select date range
            </Typography>

            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  mb: 1,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Start date
              </Typography>
              <TextField
                type="date"
                value={tempFromDate}
                onChange={(e) => setTempFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                variant="outlined"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.06)"
                        : "rgba(0, 0, 0, 0.03)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.05)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "transparent",
                      boxShadow: `0 0 0 2px ${"#f0eaff"}`,
                      borderColor: "#4c13a5",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.divider,
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  mb: 1,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                End date
              </Typography>
              <TextField
                type="date"
                value={tempToDate}
                onChange={(e) => setTempToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.06)"
                        : "rgba(0, 0, 0, 0.03)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.05)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "transparent",
                      boxShadow: `0 0 0 2px ${"#f0eaff"}`,
                      borderColor: "#4c13a5",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.divider,
                  },
                }}
              />
            </Box>

            {tempFromDate && tempToDate && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  backgroundColor: "#f0eaff",
                  border: `1px solid ${"#4c13a5"}`,
                  borderRadius: 1.5,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#4c13a5",
                    fontSize: "0.9rem",
                  }}
                >
                  {tempFromDate} → {tempToDate}
                </Typography>
              </Box>
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                onClick={handleCloseDateDropdown}
                variant="text"
                size="small"
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.9rem",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleClearDateRange}
                variant="outlined"
                size="small"
                color="error"
                sx={{
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: "0.9rem",
                  borderRadius: 1,
                }}
              >
                Clear
              </Button>
              <Button
                onClick={handleDateRangeApply}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#4c13a5",
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: "0.9rem",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "#3d0d8b",
                    boxShadow: "none",
                  },
                }}
              >
                Apply
              </Button>
            </Stack>
          </Paper>
        </Popper>
        <section
          id="tti-results"
          className="tti-card"
          aria-labelledby="tti-card-title"
          aria-busy={loading}
        >
          <div className="tti-card-heading">
            <div>
              <h2 id="tti-card-title">
                {activeTab === "ongoing" && <i className="tti-live-dot" />}
                {activePipeline.title}
              </h2>
              <p>{activePipeline.subtitle}</p>
            </div>
            <div className="tti-search-controls">
              <label className="tti-search">
                <SearchIcon />
                <input
                  aria-label="Search interviews"
                  placeholder="Search interviews"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </label>
            </div>
          </div>
          {activeTab === "all" && hasAppliedFilters && (
            <div className="tti-context">
              <span>
                {templateIdFilter
                  ? "Filtered by job opening"
                  : "Dashboard filters applied"}
              </span>
              <button
                onClick={() => {
                  resetFilters();
                  setActiveTab("all");
                }}
              >
                Clear filters
              </button>
            </div>
          )}
          <div className="tti-table-scroll">
            <table className="tti-table">
              <thead>
                <tr>
                  <th scope="col">
                    <button
                      onClick={toggleSortOrder}
                      title={`Sort ${sortOrder === "desc" ? "oldest" : "newest"} first`}
                    >
                      Date
                    </button>
                  </th>
                  <th scope="col">Time</th>
                  <th scope="col">Req ID</th>
                  <th scope="col">Manager</th>
                  <th scope="col">Recruiter</th>
                  <th scope="col">
                    <button
                      onClick={() => handleSort("job")}
                      title="Sort by title"
                    >
                      Title
                    </button>
                  </th>
                  <th scope="col">Candidate</th>
                  <th scope="col">Score</th>
                  <th scope="col">
                    <button
                      onClick={() => handleSort("stageStatus")}
                      title="Sort by stage and status"
                    >
                      Stage
                    </button>
                  </th>
                  <th scope="col">Status</th>
                  <th scope="col">Report</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={12} className="tti-empty">
                      <CircularProgress size={26} />
                      <p>Loading interviews...</p>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={12} className="tti-empty">
                      <p role="alert">{error}</p>
                      <Button
                        onClick={() => setRefreshTrigger((value) => value + 1)}
                      >
                        Retry
                      </Button>
                    </td>
                  </tr>
                ) : !visibleInterviews.length ? (
                  <tr>
                    <td colSpan={12} className="tti-empty">
                      <strong>No interviews found.</strong>
                      <p>
                        Try adjusting your filters or selecting another pipeline
                        tab.
                      </p>
                    </td>
                  </tr>
                ) : (
                  visibleInterviews.map((interview) => {
                    const date = parseLocalDate(interview.interviewDate);
                    const status = getSelectionStatus(interview);
                    const completed =
                      interview.job_application_status === "Completed";
                    const managerName = managerNameOf(interview);
                    const recruiterName = recruiterNameOf(interview);
                    const stage = interview.job_application_status || "N/A";
                    return (
                      <tr key={interview._id}>
                        <td>
                          <Tooltip
                            title={renderAuditTooltipContent({
                              primaryLabel: "Interview timezone",
                              primaryValue:
                                interview.interviewTimezone || "Local timezone",
                              createdByName: interview.createdByName,
                              updatedByName: interview.lastUpdatedByName,
                            })}
                          >
                            <span>
                              {date ? format(date, "dd/MM/yyyy") : "\u2014"}
                            </span>
                          </Tooltip>
                        </td>
                        <td
                          title={
                            interview.interviewTimezone || "Local timezone"
                          }
                        >
                          {formatTimeString(interview) || "\u2014"}
                        </td>
                        <td>
                          {interview.reqId ||
                            interview.jobTemplate?.reqId ||
                            "\u2014"}
                        </td>
                        <td>
                          <Tooltip title={managerName}>
                            <span className="tti-truncate">
                              {managerName.trim().split(/\s+/)[0]}
                            </span>
                          </Tooltip>
                        </td>
                        <td>
                          <Tooltip title={recruiterName}>
                            <span className="tti-truncate">
                              {recruiterName.trim().split(/\s+/)[0]}
                            </span>
                          </Tooltip>
                        </td>
                        <td>
                          <Tooltip
                            title={`${interview.jobTemplate?.name || interview.templateName || ""}${interview.interviewDuration ? ` \u00b7 ${interview.interviewDuration} min` : ""}`}
                          >
                            <span className="tti-truncate tti-title">
                              {interview.jobTemplate?.name ||
                                interview.templateName ||
                                "\u2014"}
                            </span>
                          </Tooltip>
                        </td>
                        <td className="tti-candidate">
                          <Tooltip title={interview.candidateName || ""}>
                            <strong className="tti-truncate">
                              {interview.candidateName || "\u2014"}
                            </strong>
                          </Tooltip>
                          {/* {interview.proctoringAlert && (
                            <Tooltip title="Proctoring alert">
                              <WarningIcon className="tti-alert-icon" />
                            </Tooltip>
                          )} */}
                        </td>
                        <td>
                          {completed && interview.overall_percentage != null ? (
                            <span
                              className={`tti-score ${interview.candidate_selection_status?.toLowerCase() === "pass" ? "is-pass" : "is-fail"}`}
                            >
                              {formatScoreForDisplay(
                                interview.overall_percentage,
                              )}
                              %
                            </span>
                          ) : (
                            <span className="tti-na">N/A</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`tti-stage tti-stage-${stage.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {stage === "In Progress" && (
                              <i className="tti-live-dot" />
                            )}
                            {stage}
                          </span>
                        </td>
                        <td>
                          <button
                            className={`tti-status tti-status-${status.toLowerCase()}`}
                            disabled={!completed}
                            aria-label={`Change status for ${interview.candidateName || "candidate"}, currently ${status.toLowerCase()}`}
                            aria-haspopup="menu"
                            onClick={(event) => {
                              setStatusAnchor(event.currentTarget);
                              setStatusInterview(interview);
                            }}
                          >
                            {status.charAt(0) + status.slice(1).toLowerCase()}
                            {completed && <KeyboardArrowDownIcon />}
                          </button>
                        </td>
                        <td>
                          <Tooltip title="View Comprehensive Report">
                            <IconButton
                              className="tti-report"
                              aria-label={`View Comprehensive Report for ${interview.candidateName || "candidate"}`}
                              onClick={() =>
                                handleViewComprehensiveReport(interview._id)
                              }
                              size="small"
                            >
                              <VisibilityOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        </td>
                        <td>
                          <Tooltip title="More actions">
                            <IconButton
                              aria-label={`Actions for ${interview.candidateName || "candidate"}`}
                              onClick={(event) =>
                                handleMenuOpen(event, interview._id)
                              }
                              size="small"
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <footer className="tti-footer">
            <span>
              Showing {loading ? 0 : visibleInterviews.length} of{" "}
              {sortedInterviews.length} results
            </span>
            <Pagination
              count={pageCount}
              page={currentPage}
              onChange={(_, next) => setPage(next)}
              shape="rounded"
              size="small"
            />
          </footer>
        </section>
      </main>
      <NotesManagerDialog
        open={notesDialogOpen}
        onClose={handleCloseNotesDialog}
        interviewId={currentInterviewId}
        candidateName={currentCandidateName}
      />
      <ConfirmationDialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Interview"
        message="Are you sure you want to delete this interview? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous
      />
      <Menu
        anchorEl={statusAnchor}
        open={Boolean(statusAnchor)}
        onClose={() => setStatusAnchor(null)}
        PaperProps={{ className: "tti-status-menu" }}
      >
        {["SHORTLISTED", "REJECTED"].map((status) => (
          <MenuItem
            key={status}
            selected={
              statusInterview && getSelectionStatus(statusInterview) === status
            }
            onClick={() => {
              handleStatusChange(statusInterview._id, status);
              setStatusAnchor(null);
            }}
          >
            <i className={`tti-status-dot tti-dot-${status.toLowerCase()}`} />
            <span>{status.charAt(0) + status.slice(1).toLowerCase()}</span>
            {statusInterview &&
              getSelectionStatus(statusInterview) === status && <CheckIcon />}
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        PaperProps={{ className: "tti-action-menu" }}
      >
        <MenuItem
          component={canEditMenuInterview ? RouterLink : "li"}
          to={
            canEditMenuInterview && menuInterview
              ? `/interviews/edit/${menuInterview._id}`
              : undefined
          }
          disabled={!canEditMenuInterview}
          onClick={handleMenuClose}
        >
          <ListItemIcon sx={{ color: "#7350cf" }}>
            <EditIcon />
          </ListItemIcon>
          Edit Interview
        </MenuItem>
        <MenuItem
          disabled={menuCompleted}
          onClick={() => {
            handleSendInvite(menuInterview);
            handleMenuClose();
          }}
        >
          <ListItemIcon sx={{ color: "#0da777" }}>
            <MarkEmailReadIcon />
          </ListItemIcon>
          Send Invitation
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDownloadResume(menuInterviewId);
            handleMenuClose();
          }}
        >
          <ListItemIcon sx={{ color: "#498cc8" }}>
            <HiDocumentDownload size={18} />
          </ListItemIcon>
          Download Resume
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleOpenNotesDialog(menuInterviewId);
            handleMenuClose();
          }}
        >
          <ListItemIcon sx={{ color: "#8e59cf" }}>
            <NotesIcon />
          </ListItemIcon>
          Manage Notes
        </MenuItem>
        <MenuItem
          disabled={!canDeleteInterview}
          onClick={() => {
            handleDelete(menuInterviewId);
            handleMenuClose();
          }}
        >
          <ListItemIcon sx={{ color: "#e64067" }}>
            <DeleteIcon />
          </ListItemIcon>
          Delete Interview
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default InterviewList;

// Archived legacy report table heading.
//                   <TableCell
//                     align="center"
//                     sx={{
//                       fontWeight: 800,
//                       whiteSpace: "nowrap",
//                       py: 1.5,
//                       px: 0.5,
//                       width: "6%",
//                       fontSize: "0.85rem",
//                       color: "text.primary",
//                       verticalAlign: "middle",
//                       letterSpacing: "0.2px",
//                     }}
//                   >
//                     Reports
//                   </TableCell>

// Archived legacy report column. Consolidated into the Comprehensive Report eye.
//                       {/* Reports Column */}
//                       <TableCell
//                         align="center"
//                         sx={{
//                           whiteSpace: "nowrap",
//                           py: 1.3,
//                           px: 0.5,
//                           width: "6%",
//                           textAlign: "center",
//                         }}
//                       >
//
//                         <Tooltip title="More actions">
//                           <span>
//                             <IconButton
//                               onClick={(e) => handleReportsMenuOpen(e, interview._id)}
//                               size="small"
//                               color="primary"
//                               sx={{ borderRadius: 1.5, color: "primary.main" }}
//                             >
//                               <MoreVertIcon fontSize="small" sx={{ color: "primary.main" }} />
//                             </IconButton>
//                           </span>
//                         </Tooltip>
//
//                         <Menu
//                           anchorEl={reportsAnchor}
//                           open={reportsInterviewId === interview._id}
//                           onClose={handleReportsMenuClose}
//                           transformOrigin={{ horizontal: "right", vertical: "top" }}
//                           anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
//                           PaperProps={{
//                             sx: {
//                               minWidth: 260,
//                               borderRadius: 2.5,
//                               boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
//                             },
//                           }}
//                         >
//
//                           {/* View Comprehensive Report */}
//                           <MenuItem
//                             onClick={() => {
//                               handleViewComprehensiveReport(reportsInterviewId);
//                               handleReportsMenuClose();
//                             }}
//                             sx={{
//                               fontWeight: 600,
//                               py: 1.5,
//                               px: 2,
//                               "&:hover": {
//                                 backgroundColor: "rgba(234, 88, 12, 0.08)",
//                               },
//                             }}
//                           >
//                             <ListItemIcon sx={{ color: "#EA580C", minWidth: 40 }}>
//                               <VideocamIcon fontSize="medium" sx={{ fontSize: "20px" }} />
//                             </ListItemIcon>
//                             <ListItemText
//                               primary="View Comprehensive Report"
//                               primaryTypographyProps={{
//                                 sx: { fontSize: "0.95rem", fontWeight: 500 },
//                               }}
//                             />
//                           </MenuItem>
//
//                           {/* View Report */}
//                           <MenuItem
//                             onClick={() => {
//                               handleViewReport(reportsInterviewId);
//                               handleReportsMenuClose();
//                             }}
//                             sx={{
//                               fontWeight: 600,
//                               py: 1.5,
//                               px: 2,
//                               "&:hover": {
//                                 backgroundColor: "rgba(124, 58, 237, 0.08)",
//                               },
//                             }}
//                           >
//                             <ListItemIcon sx={{ color: "#7C3AED", minWidth: 40 }}>
//                               <GrDocumentPdf size={20} style={{ color: "#7C3AED" }} />
//                             </ListItemIcon>
//                             <ListItemText
//                               primary="View Details Report"
//                               primaryTypographyProps={{
//                                 sx: { fontSize: "0.95rem", fontWeight: 500 },
//                               }}
//                             />
//                           </MenuItem>
//
//                           {/* Summary */}
//                           <MenuItem
//                             onClick={() => {
//                               handleViewSummaryPDF(reportsInterviewId);
//                               handleReportsMenuClose();
//                             }}
//                             sx={{
//                               fontWeight: 600,
//                               py: 1.5,
//                               px: 2,
//                               "&:hover": {
//                                 backgroundColor: "rgba(59, 130, 246, 0.08)",
//                               },
//                             }}
//                           >
//                             <ListItemIcon sx={{ color: "#3B82F6", minWidth: 40 }}>
//                               <GrDocumentPdf size={20} style={{ color: "#3B82F6" }} />
//                             </ListItemIcon>
//                             <ListItemText
//                               primary="View Summary Report"
//                               primaryTypographyProps={{
//                                 sx: { fontSize: "0.95rem", fontWeight: 500 },
//                               }}
//                             />
//                           </MenuItem>
//
//                           {/* Transcript */}
//                           <MenuItem
//                             onClick={() => {
//                               handleViewTranscriptPDF(reportsInterviewId);
//                               handleReportsMenuClose();
//                             }}
//                             sx={{
//                               fontWeight: 600,
//                               py: 1.5,
//                               px: 2,
//                               "&:hover": {
//                                 backgroundColor: "rgba(34, 197, 94, 0.08)",
//                               },
//                             }}
//                           >
//                             <ListItemIcon sx={{ color: "#22C55E", minWidth: 40 }}>
//                               <GrDocumentPdf size={20} style={{ color: "#22C55E" }} />
//                             </ListItemIcon>
//                             <ListItemText
//                               primary="View Transcript Report"
//                               primaryTypographyProps={{
//                                 sx: { fontSize: "0.95rem", fontWeight: 500 },
//                               }}
//                             />
//                           </MenuItem>
//
//                           {/* Cam 1 */}
//                           <MenuItem
//                             onClick={() => {
//                               handleViewRecording(reportsInterviewId);
//                               handleReportsMenuClose();
//                             }}
//                             sx={{
//                               fontWeight: 600,
//                               py: 1.5,
//                               px: 2,
//                               "&:hover": {
//                                 backgroundColor: "rgba(239, 68, 68, 0.08)",
//                               },
//                             }}
//                           >
//                             <ListItemIcon sx={{ color: "#EF4444", minWidth: 40 }}>
//                               <OndemandVideoIcon fontSize="medium" sx={{ fontSize: "20px" }} />
//                             </ListItemIcon>
//                             <ListItemText
//                               primary="View Cam-1 Recording"
//                               primaryTypographyProps={{
//                                 sx: { fontSize: "0.95rem", fontWeight: 500 },
//                               }}
//                             />
//                           </MenuItem>
//
//                           {/* Cam 2 */}
//                           <MenuItem
//                             onClick={() => {
//                               handleViewCam2Recording(reportsInterviewId);
//                               handleReportsMenuClose();
//                             }}
//                             sx={{
//                               fontWeight: 600,
//                               py: 1.5,
//                               px: 2,
//                               "&:hover": {
//                                 backgroundColor: "rgba(245, 158, 11, 0.08)",
//                               },
//                             }}
//                           >
//                             <ListItemIcon sx={{ color: "#F59E0B", minWidth: 40 }}>
//                               <VideocamIcon fontSize="medium" sx={{ fontSize: "20px" }} />
//                             </ListItemIcon>
//                             <ListItemText
//                               primary="View Cam-2 Recording"
//                               primaryTypographyProps={{
//                                 sx: { fontSize: "0.95rem", fontWeight: 500 },
//                               }}
//                             />
//                           </MenuItem>
//
//                         </Menu>
//
//                       </TableCell>
//
